import { idb } from "./idb";

const DEFAULT_BASE = import.meta.env.VITE_API_URL || "http://localhost:3000/api/v1";

export type TokenPair = { accessToken: string; refreshToken: string };
export type CurrentUser = {
  id: string;
  email: string | null;
  name: string;
  roleName: "manager" | "cashier";
  storeId: string | null;
  permissions?: string[];
  // Set by the API when the user must change their password before continuing
  // (bootstrap admin first login / admin-initiated reset).
  mustChangePassword?: boolean;
};

type RefreshOutcome = "success" | "unauthorized" | "network_error";
let refreshInFlight: Promise<RefreshOutcome> | null = null;

// Bumped on sign-out. A tryRefresh() that was already in flight when the
// user signed out must NOT write its fresh token pair back to IDB — that
// race is what made sign-out "not stick": a background poller 401s, the
// refresh succeeds (the server still honors the un-revoked token), and the
// session silently resurrects after logout cleared storage.
let sessionEpoch = 0;

// Listeners receive a notification whenever the local auth session is cleared
// because the server rejected our refresh token. Network failures don't fire
// it — the user might still have working pages or come back online soon.
// The auth store subscribes so it can drop the stale user and let App route
// back to LoginConnected; without this, the renderer keeps showing a logged-in
// user while every API call returns Unauthorized.
type AuthClearedListener = () => void;
const authClearedListeners = new Set<AuthClearedListener>();
export function onAuthCleared(cb: AuthClearedListener): () => void {
  authClearedListeners.add(cb);
  return () => authClearedListeners.delete(cb);
}
async function clearAuthAndNotify() {
  await idb.remove("auth.accessToken");
  await idb.remove("auth.refreshToken");
  await idb.remove("user");
  for (const cb of authClearedListeners) {
    try { cb(); } catch { /* don't let one listener break others */ }
  }
}

async function baseUrl(): Promise<string> {
  const saved = await idb.get<string>("api.baseUrl");
  return saved ?? DEFAULT_BASE;
}

async function authHeader(): Promise<Record<string, string>> {
  const token = await idb.get<string>("auth.accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function isAuthEndpoint(path: string): boolean {
  return path.startsWith("/auth/login") || path.startsWith("/auth/refresh");
}

async function tryRefresh(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const epochAtStart = sessionEpoch;
    const refreshToken = await idb.get<string>("auth.refreshToken");
    if (!refreshToken) {
      // No refresh token — session is dead. Clear and notify so the auth
      // store can route back to login.
      await clearAuthAndNotify();
      return "unauthorized" as const;
    }
    try {
      const res = await fetch(`${await baseUrl()}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        // Server reachable but refused — refresh token is dead. Clear + notify.
        await clearAuthAndNotify();
        return "unauthorized" as const;
      }
      const pair = (await res.json()) as TokenPair;
      if (epochAtStart !== sessionEpoch) {
        // User signed out while this refresh was in flight — discard the
        // pair instead of resurrecting the session.
        return "unauthorized" as const;
      }
      await idb.set("auth.accessToken", pair.accessToken);
      await idb.set("auth.refreshToken", pair.refreshToken);
      return "success" as const;
    } catch {
      // Network blip — keep the tokens; next request retries refresh.
      return "network_error" as const;
    }
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

// Full local sign-out plus best-effort server-side revoke. The epoch bump
// must come first (it fences concurrent tryRefresh writes), then storage is
// cleared, then the revoke fires without blocking — signing out while
// offline must still sign you out locally.
export async function endSession(): Promise<void> {
  sessionEpoch++;
  const headers = await authHeader();
  const base = await baseUrl();
  await idb.remove("auth.accessToken");
  await idb.remove("auth.refreshToken");
  await idb.remove("user");
  if (headers.Authorization) {
    void fetch(`${base}/auth/logout`, { method: "POST", headers }).catch(() => {
      /* server unreachable — local sign-out already done */
    });
  }
}

async function request<T>(method: string, path: string, body?: unknown, _retried = false): Promise<T> {
  const headers = { ...(await authHeader()) };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(`${await baseUrl()}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && !_retried && !isAuthEndpoint(path)) {
    const outcome = await tryRefresh();
    if (outcome === "success") return request<T>(method, path, body, true);
    if (outcome === "network_error") {
      throw new Error("Cannot reach server (refresh failed)");
    }
    // outcome === "unauthorized": fall through to the !res.ok branch which
    // surfaces the original 401 message; clearAuthAndNotify already fired.
  }
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg = (json && json.message) || `HTTP ${res.status}`;
    throw new Error(Array.isArray(msg) ? msg.join("; ") : String(msg));
  }
  return json as T;
}

function qs(query?: Record<string, unknown>): string {
  if (!query) return "";
  const entries = Object.entries(query).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  if (!entries.length) return "";
  return "?" + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

async function postFile<T = unknown>(
  path: string,
  fileBuffer: Uint8Array,
  filename: string,
  contentType: string,
  _retried = false,
): Promise<T> {
  const fd = new FormData();
  // Wrap in a Blob so the browser fills the multipart Content-Type with
  // a boundary automatically — never set Content-Type manually here.
  // Cast: TS 5.7 narrowed Uint8Array's buffer to ArrayBufferLike, but
  // Blob wants ArrayBufferView<ArrayBuffer>. Runtime is identical.
  const blob = new Blob([fileBuffer as unknown as BlobPart], { type: contentType });
  fd.append("file", blob, filename);
  const res = await fetch(`${await baseUrl()}${path}`, {
    method: "POST",
    headers: { ...(await authHeader()) },
    body: fd,
  });
  if (res.status === 401 && !_retried && !isAuthEndpoint(path)) {
    const outcome = await tryRefresh();
    if (outcome === "success") return postFile(path, fileBuffer, filename, contentType, true);
    if (outcome === "network_error") throw new Error("Cannot reach server (refresh failed)");
  }
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg = (json && json.message) || `HTTP ${res.status}`;
    throw new Error(Array.isArray(msg) ? msg.join("; ") : String(msg));
  }
  return json as T;
}

async function getBlob(path: string, query?: Record<string, unknown>, _retried = false): Promise<Blob> {
  const res = await fetch(`${await baseUrl()}${path}${qs(query)}`, {
    headers: { ...(await authHeader()) },
  });
  if (res.status === 401 && !_retried && !isAuthEndpoint(path)) {
    const outcome = await tryRefresh();
    if (outcome === "success") return getBlob(path, query, true);
    if (outcome === "network_error") throw new Error("Cannot reach server (refresh failed)");
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const txt = await res.text();
      if (txt) {
        const j = JSON.parse(txt) as { message?: unknown };
        if (j.message) msg = Array.isArray(j.message) ? j.message.join("; ") : String(j.message);
      }
    } catch {
      /* non-JSON body */
    }
    throw new Error(msg);
  }
  return res.blob();
}

export const api = {
  get: <T = unknown>(path: string, query?: Record<string, unknown>) =>
    request<T>("GET", `${path}${qs(query)}`),
  post: <T = unknown>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T = unknown>(path: string, body?: unknown) => request<T>("PUT", path, body),
  delete: <T = unknown>(path: string) => request<T>("DELETE", path),

  // Phase 4-F-Follow Wave 2b: binary download shape matches Till's
  // window.till.api.getBlob so the Till wrappers the Manager reuses see
  // the same API through main.tsx's shim.
  getBlob: (path: string, query?: Record<string, unknown>) => getBlob(path, query),

  // Phase 4-F-Follow Wave 2c: multipart upload — same signature as the
  // Till's postFile (Uint8Array bytes + filename + content-type) so the
  // shared adapter hook in @till/adapters/useProducts works under the shim.
  postFile: <T = unknown>(
    path: string,
    fileBuffer: Uint8Array,
    filename: string,
    contentType: string,
  ) => postFile<T>(path, fileBuffer, filename, contentType),

  // Auth flows mirror the Till's api-client; /auth/login returns just a token
  // pair, /auth/me is a separate call that resolves the user record.
  loginPassword: (email: string, password: string) =>
    request<TokenPair>("POST", "/auth/login", { email, password }),
  me: () => request<CurrentUser>("GET", "/auth/me"),

  // Forced-password-change flow. Clears mustChangePassword server-side on success.
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: boolean }>("POST", "/auth/change-password", { currentPassword, newPassword }),
};
